import { Stack } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Controller,
  FieldValues,
  PathValue,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/components/ui/alert/alert';
import { TFormTagsInputProps } from '@/shared/components/ui/form/components/form-tags-input/form-tags-input.types';
import { useFormTagsInputAdornment } from '@/shared/components/ui/form/components/form-tags-input/hooks/use-tags-input-adornment/use-tags-input-adornment';
import { TextField } from '@/shared/components/ui/form/components/text-field/text-field';

export const FormTagsInput = <TFieldValues extends FieldValues>({
  name,
  label,
  placeholder,
  defaultValue,
  hint,
  required,
  hideOptionalLabel,
  ...rest
}: TFormTagsInputProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TFieldValues>();
  const { getSlotProps } = useFormTagsInputAdornment();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputValueRef = useRef('');
  const tagsRef = useRef<string[]>([]);
  const onChangeRef = useRef<((value: string[]) => void) | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const _placeholder =
    placeholder ??
    t('ui:default_placeholder', {
      field: label,
    });
  const _defaultValue = (defaultValue ?? []) as PathValue<
    TFieldValues,
    typeof name
  >;

  const clearInputValue = () => {
    inputValueRef.current = '';
    setInputValue('');
  };

  const commitPendingInput = () => {
    const tag = inputValueRef.current.trim();

    if (!tag || tagsRef.current.includes(tag)) {
      return;
    }

    onChangeRef.current?.([...tagsRef.current, tag]);
    clearInputValue();
  };

  useEffect(() => {
    const form = inputRef.current?.form;

    if (!form) {
      return;
    }

    const handleSubmit = () => {
      flushSync(commitPendingInput);
    };

    form.addEventListener('submit', handleSubmit, true);

    return () => {
      form.removeEventListener('submit', handleSubmit, true);
    };
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={_defaultValue}
      render={({ field, fieldState }) => {
        // Ensure field.value is always an array
        const tags: string[] = Array.isArray(field.value) ? field.value : [];
        tagsRef.current = tags;
        onChangeRef.current = field.onChange;

        const handleAddTag = (tag: string) => {
          if (tag.trim() && !tags.includes(tag.trim())) {
            const newTags = [...tags, tag.trim()];
            field.onChange(newTags);
            return true;
          }
          return false;
        };

        const handleDeleteTag = (tagToDelete: string) => {
          const newTags = tags.filter((tag) => tag !== tagToDelete);
          field.onChange(newTags);
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          inputValueRef.current = newValue;

          // Clear existing timer
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }

          if (newValue && newValue.endsWith(',')) {
            const tagValue = newValue.slice(0, -1).trim();
            if (handleAddTag(tagValue)) {
              clearInputValue();
            } else {
              setInputValue(newValue);
            }
          } else {
            setInputValue(newValue);

            // Set new timer for auto-add after 1 second of inactivity
            if (newValue.trim()) {
              debounceTimerRef.current = setTimeout(() => {
                if (handleAddTag(newValue.trim())) {
                  clearInputValue();
                }
              }, 1000);
            }
          }
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter' && inputValue && inputValue.trim()) {
            e.preventDefault();
            // Clear the debounce timer when user presses Enter
            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current);
            }

            if (handleAddTag(inputValue.trim())) {
              clearInputValue();
            }
          }

          if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
            const newTags = [...tags];
            newTags.pop();
            field.onChange(newTags);
          }
        };

        const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
          e.preventDefault();

          // Clear the debounce timer on paste
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }

          const pastedText = e.clipboardData.getData('text');

          // Split by common separators (comma, semicolon, newline, tab)
          const values = pastedText
            .split(/[,;\n\t]/)
            .map((val) => val.trim())
            .filter((val) => val.length > 0);

          // Filter out duplicates and empty values
          const newTags = values.filter(
            (value) => value && !tags.includes(value),
          );

          // Add all new tags in a single update
          if (newTags.length > 0) {
            field.onChange([...tags, ...newTags]);
          }

          // Keep the input empty after pasting
          clearInputValue();
        };

        const handleBlur = () => {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }

          if (inputValue.trim()) {
            flushSync(() => {
              if (handleAddTag(inputValue.trim())) {
                clearInputValue();
              }
            });
          }

          field.onBlur();
        };

        return (
          <Stack gap="0.75rem" width="100%">
            <TextField
              label={label}
              {...rest}
              hideOptionalLabel={hideOptionalLabel || required}
              inputRef={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              error={!!fieldState?.error}
              helperText={fieldState.error?.message}
              fullWidth
              placeholder={tags.length === 0 ? _placeholder : ''}
              slotProps={{
                ...getSlotProps(tags, handleDeleteTag),
                htmlInput: {
                  'aria-required': required,
                },
              }}
            />
            {hint && <Alert color="blue" text={hint} />}
          </Stack>
        );
      }}
    />
  );
};
